import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Cpu, Globe, Linkedin, MapPin, Mail, Server } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function About() {
  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />

      {/* Hero */}
      <section className="relative overflow-hidden nst-navy text-white pt-[140px] pb-24">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="glow-orb w-[500px] h-[500px] -top-20 -right-20" style={{ background: "#2E6BF6" }} />
        <div className="glow-orb w-[400px] h-[400px] bottom-0 -left-20" style={{ background: "#16C47F" }} />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <span className="pill-label on-dark">About NSTI</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] max-w-4xl">
            A decade of empowering <span className="text-[#6B9FFF]">secure cloud innovation</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/75 max-w-3xl leading-relaxed">
            NSTInstitute (NSTI) is a principal-engineering practice based in Austin, Texas, focused on designing and operating intelligent cloud platforms for enterprises and growing SMBs. We build systems that scale — and teams that trust them.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/#contact" className="btn-navy" style={{ background: "#2E6BF6" }}>
              Work With Us <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/#services" className="btn-ghost-light">
              Explore Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="nst-light py-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
            {[
              { k: "10+", v: "Years of engineering" },
              { k: "99.99%", v: "Platform uptime SLA" },
              { k: "Multi-Cloud", v: "AWS / Azure / GCP" },
              { k: "Austin, TX", v: "Headquartered" },
            ].map((s) => (
              <div key={s.v} className="bg-white border border-[#E2E8F4] rounded-2xl p-7">
                <div className="text-3xl md:text-4xl font-extrabold text-[#0A1F47]">{s.k}</div>
                <div className="text-sm text-[#0A1F47]/60 mt-1 font-medium">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="nst-light pb-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="relative">
                <div
                  className="w-full aspect-square rounded-3xl bg-[#0A1F47] relative overflow-hidden"
                  style={{
                    background: "linear-gradient(135deg, #0A1F47 0%, #1C3D7A 100%)",
                  }}
                >
                  <div className="absolute inset-0 bg-grid-dark opacity-40" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-32 h-32 rounded-full bg-[#2E6BF6]/20 border-2 border-[#2E6BF6]/40 flex items-center justify-center mx-auto">
                        <span className="text-5xl font-extrabold text-white">KB</span>
                      </div>
                      <div className="mt-6 text-white text-lg font-bold">Kween Baker</div>
                      <div className="text-white/60 text-sm">Founder & Principal Engineer</div>
                    </div>
                  </div>
                  <div className="glow-orb w-[300px] h-[300px] -bottom-20 -right-20" style={{ background: "#2E6BF6" }} />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <span className="pill-label">Founder</span>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0A1F47] leading-tight">
                Kween Baker — Principal Systems & <span className="text-[#2E6BF6]">SAN Engineer</span>
              </h2>
              <div className="mt-6 space-y-4 text-[#0A1F47]/80 leading-relaxed text-[17px]">
                <p>
                  Kween Baker is the founder of NSTInstitute LABs and the developer of both StorLabs™ and SysLabs™ — platforms for system and storage infrastructure deployment used across enterprise engagements.
                </p>
                <p>
                  Alongside NSTInstitute LABs services, NSTI provides senior systems consulting for IBM’s system and storage customers. That work includes virtualization engineering for POWER5 and POWER6 LPAR frames, AIX clustering on POWER architecture, and SAN fabric clustering and installations.
                </p>
                <p>
                  Today, Kween leads NSTI’s cloud practice — designing multi-cloud platforms, AIOps frameworks, and Terraform-driven infrastructure for customers who need principal-level engineering without the overhead of a large consulting firm.
                </p>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <a href="https://linkedin.com/in/kweenbaker" target="_blank" rel="noreferrer" className="btn-ghost-dark">
                  <Linkedin className="w-4 h-4" /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="nst-navy text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-25" />
        <div className="glow-orb w-[500px] h-[500px] top-0 right-0" style={{ background: "#2E6BF6" }} />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <span className="pill-label on-dark">Our Principles</span>
          <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-3xl">
            Engineering <span className="text-[#6B9FFF]">principles</span> we ship by
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                icon: Cpu,
                title: "Real systems, not slideware",
                body: "Every recommendation comes from operating it in production. We prototype, measure, and prove before we prescribe.",
              },
              {
                icon: Server,
                title: "Platforms over point fixes",
                body: "We build paved roads — versioned modules, policy-as-code, and observability baked in — so teams get compounding leverage.",
              },
              {
                icon: Award,
                title: "Principal-level execution",
                body: "Senior engineers embed directly into your team. No hand-offs, no junior-led discovery, no inflated hourly sprawl.",
              },
              {
                icon: Globe,
                title: "Multi-cloud by default",
                body: "AWS, Azure, and GCP are building blocks — not religions. We pick the right tool for the job and make them work together.",
              },
              {
                icon: MapPin,
                title: "Austin-based, globally engaged",
                body: "Based in Austin, Texas. Supporting enterprises and SMBs across North America with remote-first delivery.",
              },
              {
                icon: Mail,
                title: "Accessible partnership",
                body: "Direct access to the engineers doing the work. Async-first communication, clear SLAs, and no account-management tax.",
              },
            ].map((v) => (
              <div key={v.title} className="bg-white/[0.04] border border-white/10 rounded-3xl p-7 backdrop-blur-sm card-lift hover:border-[#2E6BF6]/60 hover:bg-white/[0.08]">
                <div className="w-12 h-12 rounded-xl bg-[#2E6BF6]/15 border border-[#2E6BF6]/30 flex items-center justify-center mb-5">
                  <v.icon className="w-5 h-5 text-[#6B9FFF]" />
                </div>
                <h3 className="text-lg font-bold">{v.title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="nst-light py-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="relative overflow-hidden rounded-3xl bg-[#0A1F47] text-white p-10 md:p-14">
            <div className="absolute inset-0 bg-grid-dark opacity-25" />
            <div className="glow-orb w-[400px] h-[400px] -top-20 -right-20" style={{ background: "#2E6BF6" }} />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="max-w-xl">
                <h3 className="text-2xl md:text-3xl font-extrabold leading-tight">
                  Want principal-level engineers on your next platform?
                </h3>
                <p className="mt-3 text-white/70">
                  We work with a limited number of engagements at a time. Tell us about your goals.
                </p>
              </div>
              <Link to="/#contact" className="btn-navy whitespace-nowrap" style={{ background: "#2E6BF6" }}>
                Get in Touch <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
